import { useMemo, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2 } from "lucide-react";
import { trpc } from "@/lib/trpc";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { UserAvatar, userLabel } from "@/components/user-avatar";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

type UserRecord = {
  email: string;
  userId: string | null;
  name: string | null;
  role: "admin" | "member" | null;
  allowedId: string | null;
  addedAt: Date | null;
};

const NO_DEFAULT_OWNER = "none";

export function SettingsUsersPage() {
  const utils = trpc.useUtils();
  const me = trpc.auth.me.useQuery();
  const currentUserId = me.data?.id;

  const usersQuery = trpc.users.listUsers.useQuery();
  const allowedQuery = trpc.users.listAllowedEmails.useQuery();

  const [email, setEmail] = useState("");

  const addAllowed = trpc.users.addAllowedEmail.useMutation({
    onSuccess: () => {
      utils.users.listAllowedEmails.invalidate();
      setEmail("");
      toast.success("Email added");
    },
    onError: (err) => toast.error(err.message || "Failed to add"),
  });

  const removeAllowed = trpc.users.removeAllowedEmail.useMutation({
    onSuccess: () => {
      utils.users.listAllowedEmails.invalidate();
    },
    onError: (err) => toast.error(err.message || "Failed to remove"),
  });

  const deleteUser = trpc.users.deleteUser.useMutation({
    onSuccess: () => {
      utils.users.listUsers.invalidate();
    },
    onError: (err) => toast.error(err.message || "Failed to remove"),
  });

  const defaultOwnerQuery = trpc.users.getDefaultOwner.useQuery();
  const setDefaultOwner = trpc.users.setDefaultOwner.useMutation({
    onSuccess: () => {
      utils.users.getDefaultOwner.invalidate();
      toast.success("Default opportunity owner updated");
    },
    onError: (err) => toast.error(err.message || "Failed to update"),
  });

  const updateRole = trpc.users.updateRole.useMutation({
    onSuccess: () => {
      utils.users.listUsers.invalidate();
      toast.success("Role updated");
    },
    onError: (err) => toast.error(err.message || "Failed to update"),
  });

  const records: UserRecord[] = useMemo(() => {
    const map = new Map<string, UserRecord>();
    for (const a of allowedQuery.data ?? []) {
      const key = a.email.toLowerCase();
      map.set(key, {
        email: a.email,
        userId: null,
        name: null,
        role: null,
        allowedId: a.id,
        addedAt: new Date(a.createdAt),
      });
    }
    for (const u of usersQuery.data ?? []) {
      const key = u.email.toLowerCase();
      const existing = map.get(key);
      map.set(key, {
        email: u.email,
        userId: u.id,
        name: u.name ?? null,
        role: (u.role ?? "member") as "admin" | "member",
        allowedId: existing?.allowedId ?? null,
        addedAt: existing?.addedAt ?? null,
      });
    }
    return Array.from(map.values()).sort((a, b) => {
      if (!!a.userId !== !!b.userId) return a.userId ? -1 : 1;
      return a.email.localeCompare(b.email);
    });
  }, [usersQuery.data, allowedQuery.data]);

  const handleAdd = (e: React.FormEvent) => {
    e.preventDefault();
    const v = email.trim();
    if (!v) return;
    addAllowed.mutate({ email: v });
  };

  const handleRemove = async (record: UserRecord) => {
    if (record.userId) {
      await deleteUser.mutateAsync({ id: record.userId });
    }
    if (record.allowedId) {
      await removeAllowed.mutateAsync({ id: record.allowedId });
    }
    toast.success(record.userId ? "User removed" : "Removed");
  };

  const isLoading = usersQuery.isLoading || allowedQuery.isLoading;
  const isMutating = removeAllowed.isPending || deleteUser.isPending;

  return (
    <div className="mx-auto w-full max-w-4xl px-4 py-8 md:px-6">
      <header className="mb-6">
        <h2 className="text-lg font-semibold">Users</h2>
        <p className="text-sm text-muted-foreground">
          Invite users by email so they can sign in with Google, then manage their roles.
        </p>
      </header>

      <div className="mb-6 flex flex-col gap-2 rounded-lg border p-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <div className="text-sm font-medium">Default opportunity owner</div>
          <p className="text-xs text-muted-foreground">
            New opportunities are assigned to this user. Leave unset to keep the creator as
            the owner. The owner can always be changed on the opportunity itself.
          </p>
        </div>
        <Select
          value={defaultOwnerQuery.data?.userId ?? NO_DEFAULT_OWNER}
          onValueChange={(value) =>
            setDefaultOwner.mutate({
              userId: value === NO_DEFAULT_OWNER ? null : value,
            })
          }
          disabled={defaultOwnerQuery.isLoading || setDefaultOwner.isPending}
        >
          <SelectTrigger className="h-9 w-full sm:w-64">
            <SelectValue placeholder="Creator of the opportunity" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={NO_DEFAULT_OWNER}>Creator of the opportunity</SelectItem>
            {(usersQuery.data ?? []).map((u) => (
              <SelectItem key={u.id} value={u.id}>
                <span className="flex items-center gap-2">
                  <UserAvatar id={u.id} name={u.name} email={u.email} size="xs" />
                  {userLabel(u.name, u.email)}
                </span>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <form onSubmit={handleAdd} className="mb-4 flex flex-col gap-2 sm:flex-row sm:flex-wrap">
        <Input
          type="email"
          placeholder="name@noium.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="sm:min-w-[260px] sm:flex-1"
          required
        />
        <Button
          type="submit"
          disabled={addAllowed.isPending || !email.trim()}
          className="w-full sm:w-auto"
        >
          <Plus className="mr-2 h-4 w-4" />
          Invite
        </Button>
      </form>

      {isLoading ? (
        <div className="py-8 text-center text-sm text-muted-foreground">Loading…</div>
      ) : records.length === 0 ? (
        <div className="rounded-lg border border-dashed py-10 text-center text-sm text-muted-foreground">
          No users yet. Invite at least one email above.
        </div>
      ) : (
        <div className="overflow-x-auto rounded-lg border">
          <Table className="min-w-[720px]">
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Email</TableHead>
                <TableHead className="w-44">Role</TableHead>
                <TableHead className="w-16" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {records.map((r) => {
                const isSelf = !!currentUserId && r.userId === currentUserId;
                return (
                  <TableRow key={r.email}>
                    <TableCell className="font-medium">
                      <span className="flex items-center gap-2">
                        {r.userId && (
                          <UserAvatar id={r.userId} name={r.name} email={r.email} size="sm" />
                        )}
                        {r.name ?? <span className="text-muted-foreground">—</span>}
                        {isSelf && (
                          <span className="text-xs font-normal text-muted-foreground">(you)</span>
                        )}
                        {defaultOwnerQuery.data?.userId === r.userId && r.userId && (
                          <Badge variant="secondary" className="bg-sky-100 text-sky-800">
                            Default owner
                          </Badge>
                        )}
                      </span>
                    </TableCell>
                    <TableCell className="text-muted-foreground">{r.email}</TableCell>
                    <TableCell>
                      {r.userId ? (
                        <Select
                          value={r.role ?? "member"}
                          onValueChange={(value) =>
                            updateRole.mutate({
                              id: r.userId!,
                              role: value as "admin" | "member",
                            })
                          }
                          disabled={updateRole.isPending || (isSelf && r.role === "admin")}
                        >
                          <SelectTrigger className="h-8 w-36">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="admin">Admin</SelectItem>
                            <SelectItem value="member">Member</SelectItem>
                          </SelectContent>
                        </Select>
                      ) : (
                        <Badge variant="secondary" className="bg-amber-100 text-amber-800">
                          Awaiting first sign-in
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex justify-end">
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button
                              size="icon"
                              variant="ghost"
                              className="h-8 w-8 text-muted-foreground hover:text-destructive"
                              disabled={isMutating || isSelf}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>
                                {r.userId ? "Remove user?" : "Cancel invite?"}
                              </AlertDialogTitle>
                              <AlertDialogDescription>
                                <span className="font-medium">{r.email}</span>{" "}
                                {r.userId
                                  ? "will no longer be able to sign in. Records they created (opportunities, clients) will remain, but without an assigned owner."
                                  : "will be removed from the invite list and won't be able to sign in for the first time until re-added."}
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancel</AlertDialogCancel>
                              <AlertDialogAction
                                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                                onClick={() => handleRemove(r)}
                              >
                                Remove
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}
